import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';

import Header from '../../components/ui/Header';
import ProductsTable from './components/ProductsTable';
import ServicesTable from './components/ServicesTable';
import SolutionBundles from './components/SolutionBundles';
import DocumentUploadZone from './components/DocumentUploadZone';
import PartnerSelectorHeader from './components/PartnerSelectorHeader';
import BulkAssignModal from './components/BulkAssignModal';
import PortfolioComparisonPanel from './components/PortfolioComparisonPanel';
import { Helmet } from 'react-helmet';
import {
  portfolioProductsService,
  portfolioServicesService,
  solutionBundlesService,
} from '../../services/portfolioService';
import {
  productsCatalogService,
  servicesCatalogService,
} from '../../services/catalogService';
import Pagination from '../../components/ui/Pagination';
import { useToast } from '../../contexts/ToastContext';
import { usePartner } from '../../contexts/PartnerContext';
import { exportToCSV, exportToPDF } from '../../utils/exportUtils';

const PortfolioMappingInterface = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { partners, selectedPartnerId, selectedPartner, switchPartner, loading: partnersLoading } = usePartner();

  const [activeTab, setActiveTab] = useState('products');
  const [showDocumentUpload, setShowDocumentUpload] = useState(false);
  const [showBulkAssign, setShowBulkAssign] = useState(false);
  const [showComparison, setShowComparison] = useState(false);

  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [catalogServices, setCatalogServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Pagination state
  const [productPage, setProductPage] = useState(1);
  const [servicePage, setServicePage] = useState(1);
  const [bundlePage, setBundlePage] = useState(1);
  const PAGE_SIZE = 10;

  useEffect(() => {
    loadData();
  }, [selectedPartnerId]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [prods, svcs, bnds, catProds, catSvcs] = await Promise.all([
        portfolioProductsService?.getAll(selectedPartnerId || null),
        portfolioServicesService?.getAll(selectedPartnerId || null),
        solutionBundlesService?.getAll(selectedPartnerId || null),
        productsCatalogService?.getActive(),
        servicesCatalogService?.getActive(),
      ]);
      setProducts(prods);
      setServices(svcs);
      setBundles(bnds);
      setCatalogProducts(catProds);
      setCatalogServices(catSvcs);
      setProductPage(1);
      setServicePage(1);
      setBundlePage(1);
    } catch (err) {
      setError('Failed to load portfolio data. Please refresh.');
      toast?.error('Failed to load portfolio data.');
      console.error('Portfolio load error:', err);
    } finally {
      setLoading(false);
    }
  };

  // ── Portfolio Completeness Score ──────────────────────────────
  const completenessScore = useMemo(() => {
    if (!selectedPartnerId) return 0;
    let score = 0;
    if (products?.length > 0) score += 40;
    if (services?.length > 0) score += 30;
    if (bundles?.length > 0) score += 20;
    if (products?.some(p => p?.strategicMarking === 'strategic')) score += 10;
    return Math.min(score, 100);
  }, [products, services, bundles, selectedPartnerId]);

  // ── Existing product catalog IDs (for bulk assign exclusion) ──
  const existingCatalogProductIds = useMemo(() =>
    products?.map(p => p?.catalogProductId)?.filter(Boolean),
    [products]
  );

  // ── Product Handlers ──────────────────────────────────────────
  const handleAddProduct = async (productData) => {
    try {
      const newProduct = await portfolioProductsService?.create({
        ...productData,
        aiTags: ['New Entry', 'Pending Review'],
        partnerId: selectedPartnerId || null,
      });
      setProducts(prev => [newProduct, ...prev]);
      toast?.success('Product added to portfolio.');
    } catch (err) {
      console.error('Add product error:', err);
      toast?.error('Failed to add product. Please try again.');
    }
  };

  const handleEditProduct = async (id, productData) => {
    try {
      const updated = await portfolioProductsService?.update(id, productData);
      setProducts(prev => prev?.map(p => p?.id === id ? updated : p));
      toast?.success('Product updated successfully.');
    } catch (err) {
      console.error('Edit product error:', err);
      toast?.error('Failed to update product.');
    }
  };

  const handleDeleteProduct = async (id) => {
    try {
      await portfolioProductsService?.delete(id);
      setProducts(prev => prev?.filter(p => p?.id !== id));
      toast?.success('Product removed from portfolio.');
    } catch (err) {
      console.error('Delete product error:', err);
      toast?.error('Failed to delete product.');
    }
  };

  const handleMarkStrategic = async (id, marking) => {
    try {
      const updated = await portfolioProductsService?.updateStrategicMarking(id, marking);
      setProducts(prev => prev?.map(p => p?.id === id ? updated : p));
      toast?.info('Strategic marking updated.');
    } catch (err) {
      console.error('Mark strategic error:', err);
      toast?.error('Failed to update strategic marking.');
    }
  };

  // ── Bulk Assign Handler ───────────────────────────────────────
  const handleBulkAssign = async (selectedCatalogProducts) => {
    try {
      const newProducts = await portfolioProductsService?.bulkCreate(selectedCatalogProducts, selectedPartnerId);
      setProducts(prev => [...(newProducts || []), ...prev]);
      toast?.success(`${newProducts?.length} product${newProducts?.length !== 1 ? 's' : ''} assigned to portfolio.`);
    } catch (err) {
      console.error('Bulk assign error:', err);
      toast?.error('Failed to bulk assign products.');
    }
  };

  // ── Service Handlers ──────────────────────────────────────────
  const handleAddService = async (serviceData) => {
    try {
      const newService = await portfolioServicesService?.create({
        ...serviceData,
        aiRiskRating: 'medium',
        partnerId: selectedPartnerId || null,
      });
      setServices(prev => [newService, ...prev]);
      toast?.success('Service added to portfolio.');
    } catch (err) {
      console.error('Add service error:', err);
      toast?.error('Failed to add service.');
    }
  };

  const handleEditService = async (id, serviceData) => {
    try {
      const updated = await portfolioServicesService?.update(id, serviceData);
      setServices(prev => prev?.map(s => s?.id === id ? updated : s));
      toast?.success('Service updated successfully.');
    } catch (err) {
      console.error('Edit service error:', err);
      toast?.error('Failed to update service.');
    }
  };

  const handleDeleteService = async (id) => {
    try {
      await portfolioServicesService?.delete(id);
      setServices(prev => prev?.filter(s => s?.id !== id));
      toast?.success('Service removed from portfolio.');
    } catch (err) {
      console.error('Delete service error:', err);
      toast?.error('Failed to delete service.');
    }
  };

  // ── Bundle Handlers ───────────────────────────────────────────
  const handleAddBundle = async (bundleData) => {
    try {
      const newBundle = await solutionBundlesService?.create({
        ...bundleData,
        partnerId: selectedPartnerId || null,
      });
      setBundles(prev => [newBundle, ...prev]);
      toast?.success('Solution bundle created.');
    } catch (err) {
      console.error('Add bundle error:', err);
      toast?.error('Failed to add bundle.');
    }
  };

  const handleEditBundle = async (id, bundleData) => {
    try {
      const updated = await solutionBundlesService?.update(id, bundleData);
      setBundles(prev => prev?.map(b => b?.id === id ? updated : b));
      toast?.success('Bundle updated successfully.');
    } catch (err) {
      console.error('Edit bundle error:', err);
      toast?.error('Failed to update bundle.');
    }
  };

  const handleDeleteBundle = async (id) => {
    try {
      await solutionBundlesService?.delete(id);
      setBundles(prev => prev?.filter(b => b?.id !== id));
      toast?.success('Bundle removed.');
    } catch (err) {
      console.error('Delete bundle error:', err);
      toast?.error('Failed to delete bundle.');
    }
  };

  const handleDocumentUpload = (files) => {
    console.log('Uploaded files:', files);
  };

  // ── Export Handlers ───────────────────────────────────────────
  const partnerName = selectedPartner?.brand_name || selectedPartner?.legal_name || 'Partner';

  const handleExportCSV = () => {
    if (!selectedPartnerId) { toast?.warning('Select a partner first.'); return; }
    const tab = activeTab;
    if (tab === 'products') {
      exportToCSV(
        products,
        ['productName', 'category', 'oemBrand', 'modelSku', 'licensingModel', 'marginCategory', 'strategicMarking'],
        { productName: 'Product Name', category: 'Category', oemBrand: 'OEM/Brand', modelSku: 'Model/SKU', licensingModel: 'Licensing', marginCategory: 'Margin', strategicMarking: 'Strategic' },
        `${partnerName}_products.csv`
      );
    } else if (tab === 'services') {
      exportToCSV(
        services,
        ['serviceName', 'serviceCategory', 'coverageArea', 'slaLevels', 'deliveryModel', 'strategicMarking'],
        { serviceName: 'Service Name', serviceCategory: 'Category', coverageArea: 'Coverage', slaLevels: 'SLA', deliveryModel: 'Delivery', strategicMarking: 'Strategic' },
        `${partnerName}_services.csv`
      );
    } else {
      exportToCSV(
        bundles,
        ['bundleName', 'description', 'targetMarket', 'estimatedValue'],
        { bundleName: 'Bundle Name', description: 'Description', targetMarket: 'Target Market', estimatedValue: 'Est. Value' },
        `${partnerName}_bundles.csv`
      );
    }
    toast?.success('CSV exported successfully.');
  };

  const handleExportPDF = () => {
    if (!selectedPartnerId) { toast?.warning('Select a partner first.'); return; }
    const tab = activeTab;
    const summaryCards = [
      { label: 'Products', value: products?.length },
      { label: 'Services', value: services?.length },
      { label: 'Bundles', value: bundles?.length },
      { label: 'Completeness', value: `${completenessScore}%` },
    ];
    if (tab === 'products') {
      exportToPDF({
        title: `${partnerName} — Products Portfolio`,
        subtitle: `Partner: ${partnerName} | Tier: ${selectedPartner?.tier || 'N/A'}`,
        rows: products,
        columns: ['productName', 'category', 'oemBrand', 'modelSku', 'licensingModel', 'marginCategory', 'strategicMarking'],
        headers: { productName: 'Product Name', category: 'Category', oemBrand: 'OEM/Brand', modelSku: 'Model/SKU', licensingModel: 'Licensing', marginCategory: 'Margin', strategicMarking: 'Strategic' },
        summaryCards,
      });
    } else if (tab === 'services') {
      exportToPDF({
        title: `${partnerName} — Services Portfolio`,
        subtitle: `Partner: ${partnerName} | Tier: ${selectedPartner?.tier || 'N/A'}`,
        rows: services,
        columns: ['serviceName', 'serviceCategory', 'coverageArea', 'slaLevels', 'deliveryModel', 'strategicMarking'],
        headers: { serviceName: 'Service Name', serviceCategory: 'Category', coverageArea: 'Coverage', slaLevels: 'SLA', deliveryModel: 'Delivery', strategicMarking: 'Strategic' },
        summaryCards,
      });
    } else {
      exportToPDF({
        title: `${partnerName} — Solution Bundles`,
        subtitle: `Partner: ${partnerName}`,
        rows: bundles,
        columns: ['bundleName', 'description', 'targetMarket', 'estimatedValue'],
        headers: { bundleName: 'Bundle Name', description: 'Description', targetMarket: 'Target Market', estimatedValue: 'Est. Value' },
        summaryCards,
      });
    }
  };

  // Paginated slices
  const paginatedProducts = products?.slice((productPage - 1) * PAGE_SIZE, productPage * PAGE_SIZE);
  const paginatedServices = services?.slice((servicePage - 1) * PAGE_SIZE, servicePage * PAGE_SIZE);
  const paginatedBundles = bundles?.slice((bundlePage - 1) * PAGE_SIZE, bundlePage * PAGE_SIZE);

  const tabs = [
    { id: 'products', label: 'Products Mapping', icon: 'Package', count: products?.length },
    { id: 'services', label: 'Services Mapping', icon: 'Wrench', count: services?.length },
    { id: 'bundles', label: 'Solution Bundles', icon: 'Layers', count: bundles?.length }
  ];

  return (
    <>
      <Helmet>
        <title>Portfolio Mapping Interface - PartnerHub</title>
        <meta name="description" content="Map partner products, services, and solution bundles with AI-powered categorization and strategic alignment" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/20">
        <Header />

        {/* Sticky Partner Selector Header */}
        <div className="pt-[148px]">
          <PartnerSelectorHeader
            partners={partners}
            selectedPartner={selectedPartner}
            selectedPartnerId={selectedPartnerId}
            onSelectPartner={switchPartner}
            productCount={products?.length}
            serviceCount={services?.length}
            bundleCount={bundles?.length}
            completenessScore={completenessScore}
            loading={partnersLoading}
          />
        </div>

        <main className="pt-8 pb-32 px-24 lg:px-32">
          <div className="max-w-[1600px] mx-auto">

            {/* Page Header */}
            <div className="mb-24">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-16 mb-20">
                <div className="flex items-center gap-12">
                  <div className="p-12 bg-gradient-to-br from-primary via-secondary to-accent rounded-xl shadow-lg">
                    <Icon name="Map" size={28} className="text-white" />
                  </div>
                  <div>
                    <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
                      Portfolio Mapping
                    </h1>
                    <p className="text-slate-600 mt-4">
                      {selectedPartner
                        ? `Managing portfolio for ${selectedPartner?.brand_name || selectedPartner?.legal_name}`
                        : 'Select a partner to begin mapping'}
                    </p>
                  </div>
                </div>

                {/* Action Buttons */}
                {selectedPartnerId && (
                  <div className="flex flex-wrap items-center gap-10">
                    <button
                      onClick={() => setShowBulkAssign(true)}
                      className="flex items-center gap-8 px-16 py-10 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primary/90 transition-colors shadow-sm"
                    >
                      <Icon name="Zap" size={16} />
                      Bulk Add
                    </button>
                    <button
                      onClick={() => setShowComparison(!showComparison)}
                      className={`flex items-center gap-8 px-16 py-10 rounded-xl text-sm font-semibold transition-colors border ${showComparison ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'}`}
                    >
                      <Icon name="GitCompare" size={16} />
                      Compare
                    </button>
                    <div className="relative group">
                      <button className="flex items-center gap-8 px-16 py-10 bg-white text-slate-700 border border-slate-200 rounded-xl text-sm font-semibold hover:border-slate-300 transition-colors">
                        <Icon name="Download" size={16} />
                        Export
                        <Icon name="ChevronDown" size={14} />
                      </button>
                      <div className="absolute right-0 top-full mt-4 w-[160px] bg-white border border-slate-200 rounded-xl shadow-lg z-20 overflow-hidden opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                        <button
                          onClick={handleExportCSV}
                          className="w-full flex items-center gap-10 px-14 py-10 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                        >
                          <Icon name="FileSpreadsheet" size={14} className="text-emerald-600" />
                          Export CSV
                        </button>
                        <button
                          onClick={handleExportPDF}
                          className="w-full flex items-center gap-10 px-14 py-10 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                        >
                          <Icon name="FileText" size={14} className="text-red-500" />
                          Export PDF
                        </button>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowDocumentUpload(!showDocumentUpload)}
                      className="flex items-center gap-8 px-16 py-10 bg-white text-slate-700 border border-slate-200 rounded-xl text-sm font-semibold hover:border-slate-300 transition-colors"
                    >
                      <Icon name="FileUp" size={16} />
                      Documents
                    </button>
                  </div>
                )}
              </div>

              {/* Error Banner */}
              {error && (
                <div className="flex items-center gap-12 p-16 rounded-xl mb-20 text-sm bg-red-50 border border-red-200 text-red-700">
                  <Icon name="AlertCircle" size={16} className="shrink-0" />
                  {error}
                  <button onClick={() => setError('')} className="ml-auto">
                    <Icon name="X" size={14} />
                  </button>
                </div>
              )}

              {/* Comparison Panel */}
              {showComparison && (
                <div className="mb-24">
                  <PortfolioComparisonPanel
                    partners={partners}
                    onClose={() => setShowComparison(false)}
                  />
                </div>
              )}

              {/* No Partner Selected — Empty State */}
              {!selectedPartnerId && !partnersLoading && (
                <div className="flex flex-col items-center justify-center py-64 text-center">
                  <div className="p-24 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-full mb-20">
                    <Icon name="Building2" size={48} className="text-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-8">Select a Partner to Begin</h3>
                  <p className="text-slate-500 max-w-[400px] text-sm leading-relaxed">
                    Use the partner selector above to choose a partner. Once selected, you can map their products, services, and solution bundles.
                  </p>
                </div>
              )}

              {/* Tabs — only when partner selected */}
              {selectedPartnerId && (
                <div className="flex gap-8 overflow-x-auto pb-4 scrollbar-hide">
                  {tabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`
                        flex items-center gap-10 px-24 py-14 rounded-xl font-semibold text-sm whitespace-nowrap transition-all
                        ${activeTab === tab?.id
                          ? 'bg-gradient-to-r from-primary via-secondary to-accent text-white shadow-lg shadow-primary/25'
                          : 'bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900 border border-slate-200'
                        }
                      `}
                    >
                      <Icon name={tab?.icon} size={18} />
                      {tab?.label}
                      <span className={`px-8 py-2 rounded-full text-xs font-bold ${activeTab === tab?.id ? 'bg-white/20' : 'bg-slate-100 text-slate-600'}`}>
                        {tab?.count}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Loading */}
            {loading && selectedPartnerId && (
              <div className="flex items-center justify-center py-64">
                <div className="flex flex-col items-center gap-16">
                  <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
                  <p className="text-slate-500 text-sm">Loading portfolio data...</p>
                </div>
              </div>
            )}

            {/* Tab Content */}
            {!loading && selectedPartnerId && activeTab === 'products' && (
              <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
                {products?.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-48 text-center px-24">
                    <div className="p-16 bg-blue-50 rounded-full mb-16">
                      <Icon name="Package" size={32} className="text-blue-400" />
                    </div>
                    <h4 className="text-lg font-semibold text-slate-700 mb-8">No products mapped yet</h4>
                    <p className="text-slate-500 text-sm mb-20">Click <strong>+ Add Product from Catalog</strong> below or use <strong>Bulk Add</strong> to assign multiple products at once.</p>
                    <button
                      onClick={() => setShowBulkAssign(true)}
                      className="flex items-center gap-8 px-20 py-10 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primary/90 transition-colors"
                    >
                      <Icon name="Zap" size={16} />
                      Bulk Add from Catalog
                    </button>
                  </div>
                ) : null}
                <ProductsTable
                  products={paginatedProducts}
                  catalogProducts={catalogProducts}
                  onAddProduct={handleAddProduct}
                  onEditProduct={handleEditProduct}
                  onDeleteProduct={handleDeleteProduct}
                  onMarkStrategic={handleMarkStrategic}
                />
                <Pagination
                  currentPage={productPage}
                  totalPages={Math.ceil(products?.length / PAGE_SIZE)}
                  totalItems={products?.length}
                  pageSize={PAGE_SIZE}
                  onPageChange={setProductPage}
                />
              </div>
            )}

            {!loading && selectedPartnerId && activeTab === 'services' && (
              <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
                {services?.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-48 text-center px-24">
                    <div className="p-16 bg-teal-50 rounded-full mb-16">
                      <Icon name="Wrench" size={32} className="text-teal-400" />
                    </div>
                    <h4 className="text-lg font-semibold text-slate-700 mb-8">No services mapped yet</h4>
                    <p className="text-slate-500 text-sm">Click <strong>+ Add Service from Catalog</strong> below to get started.</p>
                  </div>
                ) : null}
                <ServicesTable
                  services={paginatedServices}
                  catalogServices={catalogServices}
                  onAddService={handleAddService}
                  onEditService={handleEditService}
                  onDeleteService={handleDeleteService}
                />
                <Pagination
                  currentPage={servicePage}
                  totalPages={Math.ceil(services?.length / PAGE_SIZE)}
                  totalItems={services?.length}
                  pageSize={PAGE_SIZE}
                  onPageChange={setServicePage}
                />
              </div>
            )}

            {!loading && selectedPartnerId && activeTab === 'bundles' && (
              <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden p-16">
                {bundles?.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-48 text-center px-24">
                    <div className="p-16 bg-purple-50 rounded-full mb-16">
                      <Icon name="Layers" size={32} className="text-purple-400" />
                    </div>
                    <h4 className="text-lg font-semibold text-slate-700 mb-8">No solution bundles yet</h4>
                    <p className="text-slate-500 text-sm">Click <strong>Create Bundle</strong> below to package products and services together.</p>
                  </div>
                ) : null}
                <SolutionBundles
                  bundles={paginatedBundles}
                  products={products}
                  services={services}
                  onAddBundle={handleAddBundle}
                  onEditBundle={handleEditBundle}
                  onDeleteBundle={handleDeleteBundle}
                />
                <Pagination
                  currentPage={bundlePage}
                  totalPages={Math.ceil(bundles?.length / PAGE_SIZE)}
                  totalItems={bundles?.length}
                  pageSize={PAGE_SIZE}
                  onPageChange={setBundlePage}
                />
              </div>
            )}

            {/* Document Upload Zone */}
            {showDocumentUpload && selectedPartnerId && (
              <div className="mt-24">
                <DocumentUploadZone onUpload={handleDocumentUpload} />
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Bulk Assign Modal */}
      <BulkAssignModal
        isOpen={showBulkAssign}
        onClose={() => setShowBulkAssign(false)}
        catalogProducts={catalogProducts}
        existingProductIds={existingCatalogProductIds}
        onBulkAssign={handleBulkAssign}
      />
    </>
  );
};

export default PortfolioMappingInterface;